<script setup>
import { useI18n } from 'vue-i18n'
import TrackTitle from '@/components/TrackTitle.vue'
import { podiumColor } from '@/lib/podium'
import { useTrackTitle } from '@/composables/useTrackTitle'

/**
 * One track of an event as a compact row: its laps and what the player did on it.
 *
 * An event runs on a dozen tracks at once, and a full table of them pushed the standings off the
 * first screen: these rows are half as tall and sit in the column of the player's own results (see
 * views/events/EventDetailView.vue).
 */
defineProps({
  /** Track of the event, in the shape the API returns (see EventLib). */
  track: { type: Object, required: true },
  /** The player's result on this track, when they already have one. */
  result: { type: Object, default: null },
  /**
   * The hardcore attempt on this track, while it is not finished: `running` or `abandoned` (see
   * EventStageLib). A finished attempt has a result instead.
   */
  attempt: { type: Object, default: null },
  /** Whether the stage may be erased and raced again (see EventDetailView). */
  canRetry: { type: Boolean, default: false },
  /** Whether the retry of this row is the one running. */
  retrying: { type: Boolean, default: false },
  /** Event the track belongs to: its name leads to the standings of the track. */
  eventId: { type: [String, Number], required: true },
})

defineEmits(['retry'])

const { t } = useI18n()
const { fullName } = useTrackTitle()
</script>

<template>
  <div class="track-row d-flex align-center ga-2 pa-3">
    <RouterLink
      :to="{ name: 'event_track', params: { id: eventId, trackKey: track.trackKey } }"
      :title="fullName(track.trackKey)"
      class="track-row__name flex-grow-1 text-body-2 font-weight-medium text-high-emphasis text-decoration-none text-truncate"
    >
      <TrackTitle :track-id="track.trackKey" />
    </RouterLink>

    <v-btn
      v-if="canRetry"
      icon="mdi-restart"
      size="x-small"
      variant="text"
      color="warning"
      :loading="retrying"
      :title="t('events.retryStage')"
      @click="$emit('retry')"
    />
    
    <v-chip
      v-if="result"
      size="x-small"
      variant="tonal"
      :color="podiumColor(result.position) ?? 'primary'"
      label
      :title="`${t('events.position')} ${result.position}`"
    >
      {{ t('events.position') }}{{ result.position }}
    </v-chip>

    <!-- A hardcore stage that is not finished occupies the track until a retry gives it back. -->
    <v-chip
      v-else-if="attempt"
      size="small"
      variant="tonal"
      :color="attempt.status === 'running' ? 'info' : 'error'"
      label
    >
      {{ t(`events.stageState.${attempt.status}`) }}
    </v-chip>

    <span v-else class="text-caption text-medium-emphasis text-no-wrap">{{ t('events.todo') }}</span>

    <v-icon size="18">mdi-chevron-right</v-icon>
  </div>
</template>

<style scoped>
.track-row__name {
  min-width: 0;
}

.track-row + .track-row {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
